import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../useAuth';
import { useVideo } from '../useVideo';
import { useGoogleCallback } from './useGoogleCallback';
import { useMobileDetection } from '../useUI';
import { useResumeProcessing } from '../useAnalysis';
import { useAnalysisHandler } from '../useAnalysis';
import { useHistorySelection } from '../useHistory';
import { useGlobalCopyModal } from '../useModal';

export const useLandingPageState = () => {
  const { t, i18n } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Auth state
  const authState = useAuth();

  // Video state
  const videoState = useVideo(authState.isLoggedIn ? authState.userId : null);

  // Google callback
  useGoogleCallback();

  // Mobile detection
  const isMobile = useMobileDetection();

  // Resume processing
  const resumeState = useResumeProcessing(
    authState.isLoggedIn,
    authState.userId,
    videoState.history
  );

  // Analysis handler
  const analysisState = useAnalysisHandler(
    authState.isLoggedIn,
    videoState.loading,
    videoState.analyzeLoading,
    videoState.handleAnalyze,
    authState.setLoginDialogOpen
  );

  // History selection
  const historyState = useHistorySelection(
    videoState.history,
    videoState.selectedIdx,
    videoState.setSelectedIdx
  );

  // Global copy modal
  const globalCopyState = useGlobalCopyModal(
    videoState.globalCopyModal,
    videoState.setGlobalCopyModal,
    videoState.history
  );

  // Share Extension에서 로그인 모달 자동 띄우기
  useEffect(() => {
    console.log('🔍 [Landing] Share Extension 로그인 모달 자동 띄우기 체크');
    
    // 로그인되지 않은 상태에서만 확인
    if (!authState.isLoggedIn) {
      const pendingAnalysisData = sessionStorage.getItem('pendingAnalysis');
      
      if (pendingAnalysisData) {
        try {
          const data = JSON.parse(pendingAnalysisData);
          console.log('📋 [Landing] 대기 중인 분석 데이터:', data);
          
          if (data.showLoginModal) {
            console.log('🔐 [Landing] 로그인 모달 자동 띄우기');
            
            // 로그인 모달 띄우기
            authState.setLoginDialogOpen(true);
            
            // showLoginModal 플래그 제거 (한 번만 실행되도록)
            const updatedData = { ...data };
            delete updatedData.showLoginModal;
            sessionStorage.setItem('pendingAnalysis', JSON.stringify(updatedData));
            
            console.log('✅ [Landing] 로그인 모달 자동 띄우기 완료');
          }
        } catch (error) {
          console.error('❌ [Landing] 대기 분석 데이터 파싱 실패:', error);
          sessionStorage.removeItem('pendingAnalysis');
        }
      }
    }
  }, [authState.isLoggedIn, authState.setLoginDialogOpen]);

  return {
    // Basic state
    t,
    i18n,
    drawerOpen,
    setDrawerOpen,
    navigate,
    location,
    isMobile,
    
    // Auth state
    ...authState,
    
    // Video state
    ...videoState,
    
    // Resume state
    ...resumeState,
    
    // Analysis state
    ...analysisState,
    
    // History state
    ...historyState,
    
    // Global copy state
    ...globalCopyState
  };
}; 